<script lang="ts" setup>
import type { TopListing } from './PrOverviewTopListingsCard.vue'
import { formatValue } from '@forgd/formatter'
import { Badge } from '@forgd/ui-kit/badge'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { ArrowRightIcon } from '@heroicons/vue/24/outline'
import { useQuery } from '@pinia/colada'

const TOP_LISTINGS_LIMIT = 3

// The by-Month chart's title is explicitly "Past 12 months", so it stays
// scoped to that window. The by-Exchange chart + badge count show all-time
// data (ENG-764). PAST_12_MONTHS_START is computed once at setup so the
// cache key stays stable across the page's lifetime.
const PAST_12_MONTHS_START = (() => {
  const d = new Date()
  d.setMonth(d.getMonth() - 12)
  return d.toISOString().slice(0, 10)
})()

const listingsAllTimeQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/listings-by-exchange', {
    params: () => ({ query: {} }),
  }),
  refetchOnWindowFocus: false,
})

const listingsByMonthQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/listings-by-exchange', {
    params: () => ({ query: { startDate: PAST_12_MONTHS_START } }),
  }),
  refetchOnWindowFocus: false,
})

const byExchange = computed(() => listingsAllTimeQuery.data.value?.byExchange ?? [])
const byMonth = computed(() => listingsByMonthQuery.data.value?.byMonth ?? [])

const topPrimaryQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/top-listings', {
    params: () => ({ query: { listingType: 'primary' as const, limit: TOP_LISTINGS_LIMIT } }),
  }),
  refetchOnWindowFocus: false,
})

const topFollowOnQuery = useQuery({
  ...buildApiQuerySSRSafe('/public-research/top-listings', {
    params: () => ({ query: { listingType: 'follow-on' as const, limit: TOP_LISTINGS_LIMIT } }),
  }),
  refetchOnWindowFocus: false,
})

type TopListingRow = NonNullable<typeof topPrimaryQuery.data.value>['tokens'][number]

// Maps a backend `{ date, value }[]` series to ChartSparkline-ready `[{ x, y }]`.
function toSparkline(series: TopListingRow['priceChangeTimeSeries']) {
  return series
    .map((point, i) => (typeof point.value === 'number' ? { x: i, y: point.value } : null))
    .filter((d): d is { x: number, y: number } => d !== null)
}

function toTopListings(rows: TopListingRow[] | undefined): TopListing[] {
  return (rows ?? []).map(row => ({
    rank: row.rank,
    ticker: row.symbol,
    projectLogo: row.logoUrl,
    exchangeName: row.listedOnExchange,
    day1VolumeUsd: row.day1VolumeUsd ?? 0,
    pricePerformance7d: row.priceChange7dPercentage ?? 0,
    trend: toSparkline(row.priceChangeTimeSeries),
  }))
}

const topPrimaryListings = computed(() => toTopListings(topPrimaryQuery.data.value?.tokens))
const topFollowOnListings = computed(() => toTopListings(topFollowOnQuery.data.value?.tokens))

const topListingsLoading = computed(() => topPrimaryQuery.isLoading.value || topFollowOnQuery.isLoading.value)
const hasTopListings = computed(() => topPrimaryListings.value.length > 0 || topFollowOnListings.value.length > 0)
</script>

<template>
  <section class="flex flex-col gap-5">
    <header class="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-5">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <h2 class="text-sm font-semibold text-primal">
            Exchange Intelligence
          </h2>
          <Badge v-if="byExchange.length">
            Top-{{ formatValue(byExchange.length, 'string:plural,exchange') }}
          </Badge>
        </div>
        <p class="text-xs text-secondary">
          Primary &amp; follow-on listings insights.
        </p>
      </div>
      <Button as-child variant="outline" size="sm" class="self-start sm:self-end">
        <NuxtLink to="/research/exchange-and-listing">
          View Live Report
          <ArrowRightIcon aria-hidden="true" />
        </NuxtLink>
      </Button>
    </header>

    <PrOverviewExchangeTiles :listings-start-date="PAST_12_MONTHS_START" />

    <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <Card class="flex flex-col">
        <CardHeader class="flex flex-row items-center justify-between border-b border-default p-3 sm:pl-5">
          <CardTitle>
            Primary &amp; Follow-On Listings, by Exchange
          </CardTitle>
          <Button as-child variant="outline" size="sm" icon-only aria-label="View by-exchange details">
            <NuxtLink to="/research/exchange-and-listing#listings-by-exchange">
              <ArrowRightIcon aria-hidden="true" />
            </NuxtLink>
          </Button>
        </CardHeader>

        <!-- :key force-remounts the chart when data first arrives so
             useChartLogoPositions runs on a chart whose 14 ticks are already
             in DOM — matches the detail page's PrQueryState mount semantics. -->
        <div class="p-3 sm:p-4">
          <PrListingsByExchangeChart :key="byExchange.length > 0 ? 'by-exchange' : undefined" :by-exchange="byExchange" />
        </div>
      </Card>

      <Card class="flex flex-col">
        <CardHeader class="flex flex-row items-center justify-between border-b border-default p-3 sm:pl-5">
          <CardTitle>
            Primary &amp; Follow-On Listings, by Month (Past 12 months)
          </CardTitle>
          <Button as-child variant="outline" size="sm" icon-only aria-label="View by-month details">
            <NuxtLink to="/research/exchange-and-listing#listings-by-month">
              <ArrowRightIcon aria-hidden="true" />
            </NuxtLink>
          </Button>
        </CardHeader>

        <div class="p-3 sm:p-4">
          <PrListingsByMonthChart :by-month="byMonth" />
        </div>
      </Card>
    </div>

    <div v-if="topListingsLoading || hasTopListings" class="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <PrOverviewTopListingsCard
        :title="`Top-${TOP_LISTINGS_LIMIT} Primary Listings`"
        :listings="topPrimaryListings"
        :loading="topPrimaryQuery.isLoading.value"
      />
      <PrOverviewTopListingsCard
        :title="`Top-${TOP_LISTINGS_LIMIT} Follow-On Listings`"
        :listings="topFollowOnListings"
        :loading="topFollowOnQuery.isLoading.value"
      />
    </div>
  </section>
</template>
